import { createApi } from '@reduxjs/toolkit/query/react'
import baseQuery from './baseQuery'

/**
 * Stuck Shipments panel (Reports & Analytics → Stuck Shipments).
 * Backend: proship_mongo `/api/ops/stuck-shipments/*` — ROLE_PROSHIPADMIN + STUCK_SHIPMENTS module.
 * Endpoints return the DTO directly (not wrapped in GenericAPIResponse), hence `res?.data || res`.
 */
const apiBase = 'ops/stuck-shipments'

/**
 * Same wrapper as freightPnlApiSlice: the shared baseQuery throws the raw axios error and RTK Query keeps only its
 * message, so backend texts ("The snapshot is only 3 minutes old…", "STUCK_SHIPMENTS access is required…") never
 * reached the page. Return an RTK-style error so `error.status` and `error.data.message` survive.
 */
const stuckBaseQuery = async (args, api, extra) => {
  try {
    return await baseQuery(args, api, extra)
  } catch (err) {
    const status = err?.response?.status
    const data = err?.response?.data
    return { error: { status, data, message: data?.message || err?.message || 'Request failed' } }
  }
}

export const stuckShipmentsApi = createApi({
  reducerPath: 'stuckShipmentsApi',
  baseQuery: stuckBaseQuery,
  keepUnusedDataFor: 5 * 60,
  endpoints: (builder) => ({

    // { segment, thresholdHours, merchantOids, courierOids, statusGroups, orderStatuses, buckets, breachedOnly, appointmentOnly }
    getStuckSummary: builder.query({
      query: (scope) => ({ url: `${apiBase}/summary`, data: scope, type: 'POST' }),
      transformResponse: (res) => res?.data || res,
    }),

    // same scope + { search, page (1-based), pageSize }
    getStuckShipments: builder.query({
      query: (scope) => ({ url: `${apiBase}/shipments`, data: scope, type: 'POST' }),
      transformResponse: (res) => res?.data || res,
      keepUnusedDataFor: 60,
    }),

    // → 202 with { requestId, state, ... }; 409 when running or too fresh
    refreshStuckSnapshot: builder.mutation({
      query: () => ({ url: `${apiBase}/refresh`, data: {}, type: 'POST' }),
      transformResponse: (res) => res?.data || res,
    }),

    getStuckRefreshStatus: builder.query({
      query: (requestId) => ({ url: `${apiBase}/refresh/${requestId}`, type: 'GET' }),
      transformResponse: (res) => res?.data || res,
      keepUnusedDataFor: 0,
    }),
  }),
})

export const {
  useGetStuckSummaryQuery,
  useGetStuckShipmentsQuery,
  useRefreshStuckSnapshotMutation,
  useGetStuckRefreshStatusQuery,
} = stuckShipmentsApi
